import React, { useEffect, useState } from "react";
import CuSpinner from "../../common_component/component/CuSpinner";
import "../css/patientDetails.css";
import { useUserItemsFilter } from "../../_helper/actions/app/items";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import { useCurrentRole } from "../../_helper/constant/Permission";
import { useAtom } from "jotai";
import { clinicAtom } from "../../_helper/constant/constant-atom";
import { ApiEndPoint } from "../../_helper/constant/api-types";
import { toast } from "react-toastify";
import { cufrontIcons } from "../../cufront-assets/icons/Icons";
import { useDataUpdate } from "../../_helper/actions/mutation/items";
import moment from "moment";
import { calculateAgeString } from "../../_helper/utils/utils";

const PatientDetailsComponent = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [clinic] = useAtom(clinicAtom);
  const { role_id } = useCurrentRole();
  const [searchParams] = useSearchParams();
  const userId = searchParams.get("userId");
  const filter = `?action=getDoctorPatients&backend=True&clinic_id=${clinic?.id}&doctor_id=${role_id}&clinic_ids=${clinic?.id}&id=${userId}`;
  const { data, isLoading } = useUserItemsFilter(
    ApiEndPoint.USERS,
    filter,
    clinic && role_id && userId ? true : false
  );

  const [editing, setEditing] = useState(false);
  const [patientData, setPatientData] = useState(data);
  const [editedData, setEditedData] = useState({});

  const familyFilter = `?action=getDoctorPatients&backend=True&clinic_id=${clinic?.id}&doctor_id=${role_id}&clinic_ids=${clinic?.id}&family_id=${patientData?.family_id}`;
  const { data: familyData, isLoading: isFamilyLoading } = useUserItemsFilter(
    ApiEndPoint.USERS,
    familyFilter,
    patientData?.family_id ? true : false
  );

  useEffect(() => {
    if (data) {
      setPatientData(data);
    }
  }, [data]);

  const handleEdit = () => {
    setEditing(true);
  };

  const handleCancel = () => {
    setEditing(false);
    setEditedData({});
  };

  const updateUser = useDataUpdate(ApiEndPoint.USERS);

  const validateEmail = (email) => {
    const regex = /\S+@\S+\.\S+/;
    return regex.test(email);
  };
  const validBloodGroups = ["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"];
  const handleSave = () => {
    if (editedData.email && !validateEmail(editedData.email)) {
      toast.error("Please enter a valid email address.");
      return;
    }

    if (
      editedData.full_name &&
      editedData?.full_name?.split(" ").filter(Boolean).length < 2
    ) {
      toast.error("Please enter both first name and last name.");
      return;
    }

    if (editedData.full_name && editedData?.full_name.length > 50) {
      toast.error("Name is too long.");
      return;
    }

    if (editedData.phone && editedData.phone.length !== 10) {
      toast.error("Please enter valid phone number.");
      return;
    }

    if (
      editedData.blood_group &&
      !validBloodGroups.includes(editedData.blood_group.toUpperCase())
    ) {
      toast.error("Please enter a valid blood group.");
      return;
    }

    if (editedData.zip_code?.length > 8) {
      toast.error("Invalid pincode");
      return;
    }

    if (editedData.dob) {
      const dob = moment(editedData.dob);
      const age = moment().diff(dob, "years");

      if (age > 150 || age < 0) {
        toast.error(
          "Invalid date of birth. Age cannot exceed 150 years or be less than 1 year."
        );
        return;
      }
    }

    const allowedFields = [
      "full_name",
      "dob",
      "age",
      "phone",
      "email",
      "gender",
      "blood_group",
      "address",
      "city",
      "zip_code",
      "state",
      "alternate_phone",
    ];

    const payload = {
      action: "patchUserDetails",
      clinic_id: clinic?.id,
      frontend: "True",
      id: userId,
      ...Object.keys(patientData)
        .filter((key) => allowedFields.includes(key))
        .reduce((obj, key) => {
          obj[key] = patientData[key];
          return obj;
        }, {}),
      ...editedData,
    };

    updateUser.mutate(payload, {
      onSuccess: (data) => {
        toast.success("Changes saved successfully!");
        setEditing(false);
        setEditedData({});
      },
      onError: (error) => {
        toast.error("Error occurred while saving changes!");
      },
    });
  };

  const handleInputChange = (e, field) => {
    let newValue = e.target.value;

    const specialCharRegex = /[^a-zA-Z0-9 ]/g;
    const nameCityRegex = /[^a-zA-Z ]/g;
    if (field === "phone" || field === "alternate_phone") {
      newValue = newValue.replace(/[^0-9]/g, "");
    } else if (field === "full_name" || field === "city") {
      newValue = newValue.replace(specialCharRegex, "");
      newValue = newValue.replace(nameCityRegex, "");
    } else if (field === "blood_group") {
      newValue = newValue.trim().toUpperCase();
    } else if (field === "dob") {
      const dob = moment(newValue);
      const age = moment().diff(dob, "years");
      if (age > 150) {
        newValue = moment().subtract(150, "years").format("YYYY-MM-DD");
        setEditedData({
          ...editedData,
          dob: newValue,
          age: 150,
        });
        return;
      } else {
        setEditedData({
          ...editedData,
          dob: dob.format("YYYY-MM-DD"),
          age: age,
        });
        return;
      }
    } else if (field === "age") {
      let age = parseInt(newValue, 10);
      if (age > 150) age = 150;
      else if (age < 1) age = 1;
      const dob = moment().subtract(age, "years").format("YYYY-MM-DD");
      setEditedData({
        ...editedData,
        age: age,
        dob: dob,
      });
      return;
    }

    setEditedData({
      ...editedData,
      [field]: newValue,
    });
  };

  const addQueryParam = (key, value) => {
    searchParams.set(key, value);
    navigate({
      pathname: location.pathname,
      search: searchParams.toString(),
    });
  };

  const renderInputField = (label, value, field, type, max, min) => {
    if (!editing && !value) return null;

    return (
      <div className="cu-all-patient-details">
        <div className="2xl:text-[15.81px] xl:text-[14.81px] lg:text-[11.85px] min-[1920px]:text-[22.22px]">
          {label}:
        </div>
        {editing ? (
          field === "gender" ? (
            <select
              className="cu-all-patient-details-input w-full px-2 lg:w-[260.74px] lg:h-[28.44px] xl:w-[325.93px] xl:h-[35.56px] 2xl:w-[347.83px] 2xl:h-[37.95px] min-[1920px]:w-[488.89px] min-[1920px]:h-[53.33px]"
              value={
                editedData[field] !== undefined
                  ? editedData[field]
                  : value || ""
              }
              onChange={(e) => handleInputChange(e, field)}
            >
              <option value="" disabled>
                Select Gender
              </option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
            </select>
          ) : field === "blood_group" ? (
            <select
              className="cu-all-patient-details-input w-full px-2 lg:w-[260.74px] lg:h-[28.44px] xl:w-[325.93px] xl:h-[35.56px] 2xl:w-[347.83px] 2xl:h-[37.95px] min-[1920px]:w-[488.89px] min-[1920px]:h-[53.33px]"
              value={
                editedData[field] !== undefined
                  ? editedData[field]
                  : value || ""
              }
              onChange={(e) => handleInputChange(e, field)}
            >
              <option value="" disabled>
                Select Blood Group
              </option>
              {validBloodGroups.map((item, i) => (
                <option key={i} value={item}>
                  {item}
                </option>
              ))}
            </select>
          ) : (
            <input
              className="cu-all-patient-details-input w-full pr-2 lg:w-[260.74px] lg:h-[28.44px] xl:w-[325.93px] xl:h-[35.56px] 2xl:w-[347.83px] 2xl:h-[37.95px] min-[1920px]:w-[488.89px] min-[1920px]:h-[53.33px]"
              type={type}
              value={
                editedData[field] !== undefined ? editedData[field] : value
              }
              onChange={(e) => handleInputChange(e, field)}
              max={max}
              min={min}
            />
          )
        ) : (
          <div className="2xl:text-[15.81px] xl:text-[14.81px] lg:text-[11.85px] min-[1920px]:text-[22.22px]">
            {field === "dob" ? moment(value).format("DD MMM YYYY") : value}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="cu-patient-details-main-container">
      {!editing && (
        <div
          className="flex md:hidden justify-end items-center gap-2 px-[10px]"
          onClick={handleEdit}
        >
          <img
            style={{ width: "15px", height: "15px" }}
            src={cufrontIcons.editPen}
            alt=""
          />
          <div>Edit</div>
        </div>
      )}
      {isLoading || isFamilyLoading ? (
        <CuSpinner />
      ) : (
        <>
          {renderInputField(
            "Patient Name",
            patientData?.full_name,
            "full_name"
          )}
          {renderInputField(
            "Date of Birth",
            patientData?.dob,
            "dob",
            "date",
            moment().format("YYYY-MM-DD"),
            "1900-01-01"
          )}
          {renderInputField(
            "Age",
            calculateAgeString(patientData?.dob),
            "age",
            "number"
          )}
          {renderInputField(
            "Contact Number",
            patientData?.phone,
            "phone",
            "text"
          )}
          {renderInputField("Email", patientData?.email, "email", "email")}
          {renderInputField("Gender", patientData?.gender, "gender")}
          {renderInputField(
            "Blood Group",
            patientData?.blood_group,
            "blood_group"
          )}
          {renderInputField(
            "Street/ Locality",
            patientData?.address,
            "address"
          )}
          {renderInputField("City", patientData?.city, "city")}
          {renderInputField(
            "Pincode",
            patientData?.zip_code,
            "zip_code",
            "number"
          )}
          {renderInputField("State", patientData?.state, "state")}
          {renderInputField(
            "Alternate Contact",
            patientData?.alternate_phone,
            "alternate_phone",
            "text"
          )}
          {familyData && familyData[0]?.institution_name && (
            <div className="cu-all-patient-details">
              <div className="2xl:text-[15.81px] xl:text-[14.81px] lg:text-[11.85px] min-[1920px]:text-[22.22px]">
                Institution Name:
              </div>
              <div className="2xl:text-[15.81px] xl:text-[14.81px] lg:text-[11.85px] min-[1920px]:text-[22.22px]">
                {familyData[0]?.institution_name}
              </div>
            </div>
          )}
          <div className="cu-all-patient-details">
            <div>Family Profiles:</div>
            <div>
              <div>
                {familyData?.map((item, i) => {
                  return (
                    <div
                      key={i}
                      className="cu-patient-details-family-name"
                      onClick={() => addQueryParam("userId", item?.id)}
                    >
                      {item?.full_name}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </>
      )}
      {editing && (
        <div className="flex sm:hidden my-5 m-auto max-w-[340px] h-[45px] justify-between items-center gap-3">
          <button onClick={handleCancel} className="flex-1 h-11 rounded border">
            Cancel
          </button>
          <button
            id="cu-all-patient-edit-btn-save"
            onClick={handleSave}
            style={{ backgroundColor: "#EBF8EC" }}
            className="bg-[#EBF8EC] flex-1 h-11 roundede border"
          >
            {updateUser.isLoading ? <CuSpinner /> : "Save"}
          </button>
        </div>
      )}
      {editing ? (
        <div className="cu-all-patient-edit-btn xl:right-[8%] lg:right-[5%]">
          <button onClick={handleCancel}>Cancel</button>
          <button
            id="cu-all-patient-edit-btn-save "
            onClick={handleSave}
            style={{ backgroundColor: "#EBF8EC" }}
          >
            {updateUser.isLoading ? <CuSpinner /> : "Save"}
          </button>
        </div>
      ) : (
        <div
          className="cu-patient-details-edit hidden md:flex"
          onClick={handleEdit}
        >
          <img
            style={{ width: "15px", height: "15px" }}
            src={cufrontIcons.editPen}
            alt=""
          />
          <div>Edit</div>
        </div>
      )}
    </div>
  );
};

export default PatientDetailsComponent;
